import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Profile } from "@/hooks/useMatch";
import { PriorityBadges } from "@/components/matchmaker/PriorityBadges";
import { useSignedPhotoUrl, useSignedPhotoUrls } from "@/lib/photoUrl";

interface MatchProfileDetailProps {
  profile: Profile | null | undefined;
}

export function MatchProfileDetail({ profile }: MatchProfileDetailProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const profilePicture = profile?.profile_picture_url || "";
  const galleryImages = profile?.profile_pictures && profile.profile_pictures.length > 0
    ? profile.profile_pictures
    : [];

  // Hooks must be called unconditionally; resolve signed URLs above the
  // early-return below.
  const signedProfilePicture = useSignedPhotoUrl(profilePicture);
  const signedGalleryImages = useSignedPhotoUrls(galleryImages);

  if (!profile) {
    return null;
  }

  const firstName = profile.first_name || "Unknown";
  const age = profile.age || "?";
  const gender = profile.gender || "unknown";

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="space-y-6">
      {/* Header with Profile Picture Only */}
      <div className="flex items-start gap-4">
        <Avatar className="h-32 w-32">
          {signedProfilePicture ? (
            <AvatarImage
              src={signedProfilePicture}
              alt={firstName}
            />
          ) : null}
          <AvatarFallback className="text-3xl">
            {firstName.charAt(0).toUpperCase() || "?"}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1">
          <h3 className="text-2xl font-bold mb-1">{firstName}, {age}</h3>
          <p className="text-muted-foreground capitalize">
            {gender === "male" ? "Brother" : gender === "female" ? "Sister" : "User"} • {profile.location || "Location not specified"}
          </p>
        </div>
      </div>

      {/* Photo Gallery */}
      {galleryImages.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-semibold text-lg">Photos</h4>
          <Card className="p-4">
            <div className="relative w-full">
              <div className="aspect-[4/3] w-full bg-muted rounded-lg overflow-hidden">
                {signedGalleryImages?.[currentImageIndex] ? (
                  <img
                    src={signedGalleryImages[currentImageIndex] ?? undefined}
                    alt={`${firstName} - Photo ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : null}
              </div>
              {galleryImages.length > 1 && (
                <>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full shadow-lg"
                    onClick={prevImage}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full shadow-lg"
                    onClick={nextImage}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium">
                    {currentImageIndex + 1} / {galleryImages.length}
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>
      )}

      <Separator />

      {/* Basic Info */}
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Basic Information</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">Ethnicity:</span>
            <p className="font-medium">{profile.ethnicity || "Not specified"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Marital Status:</span>
            <p className="font-medium capitalize">{profile.marital_status || "Not specified"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Citizenship:</span>
            <p className="font-medium">{profile.citizenship_status || "Not specified"}</p>
          </div>
          {profile.height_feet && (
            <div>
              <span className="text-muted-foreground">Height:</span>
              <p className="font-medium">{profile.height_feet}'{profile.height_inches || 0}"</p>
            </div>
          )}
          {profile.has_children !== undefined && (
            <div>
              <span className="text-muted-foreground">Has Children:</span>
              <p className="font-medium">
                {profile.has_children ? `Yes (${profile.children_count || 0})` : "No"}
              </p>
            </div>
          )}
          {profile.willing_to_relocate !== undefined && (
            <div>
              <span className="text-muted-foreground">Willing to Relocate:</span>
              <p className="font-medium">{profile.willing_to_relocate ? "Yes" : "No"}</p>
            </div>
          )}
        </div>
      </div>

      <Separator />

      {/* Religious Profile */}
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Religious Profile</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">Religiosity:</span>
            <p className="font-medium capitalize">{profile.religiosity_level || "Not specified"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Prayer Frequency:</span>
            <p className="font-medium">{profile.prayer_frequency || "Not specified"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Sect:</span>
            <p className="font-medium capitalize">{profile.sect || "Not specified"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Masjid Attendance:</span>
            <p className="font-medium">{profile.masjid_attendance || "Not specified"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Quran Knowledge:</span>
            <p className="font-medium">{profile.quran_knowledge || "Not specified"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Islamic Knowledge:</span>
            <p className="font-medium">{profile.islamic_knowledge_level || "Not specified"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Importance of Islam:</span>
            <p className="font-medium">{profile.importance_of_islam || "?"}/10</p>
          </div>
          {profile.gender === "male" && profile.has_beard && (
            <div>
              <span className="text-muted-foreground">Has Beard:</span>
              <p className="font-medium capitalize">{profile.has_beard}</p>
            </div>
          )}
          {profile.gender === "female" && profile.wears_hijab !== undefined && (
            <>
              <div>
                <span className="text-muted-foreground">Wears Hijab:</span>
                <p className="font-medium">{profile.wears_hijab ? "Yes" : "No"}</p>
              </div>
              {profile.wears_hijab && profile.wears_hijab_level && (
                <div>
                  <span className="text-muted-foreground">Hijab Level:</span>
                  <p className="font-medium capitalize">{profile.wears_hijab_level}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Separator />

      {/* Education & Career */}
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Education & Career</h4>
        <div className="grid gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">Education Level:</span>
            <p className="font-medium">{profile.education_level || "Not specified"}</p>
          </div>
          {profile.field_of_study && (
            <div>
              <span className="text-muted-foreground">Field of Study:</span>
              <p className="font-medium">{profile.field_of_study}</p>
            </div>
          )}
          {profile.employment_status && (
            <div>
              <span className="text-muted-foreground">Employment Status:</span>
              <p className="font-medium capitalize">{profile.employment_status}</p>
            </div>
          )}
          {profile.occupation && (
            <div>
              <span className="text-muted-foreground">Occupation:</span>
              <p className="font-medium">{profile.occupation}</p>
            </div>
          )}
          {profile.career_description && (
            <div>
              <span className="text-muted-foreground">Career Description:</span>
              <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                {profile.career_description}
              </p>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Financial Status:</span>
            <p className="font-medium">{profile.financial_status || "?"}/10</p>
          </div>
        </div>
      </div>

      <Separator />

      {/* Lifestyle */}
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Lifestyle</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">Smoking:</span>
            <p className="font-medium capitalize">{profile.smoking || "Not specified"}</p>
          </div>
          {profile.exercise_routine && (
            <div>
              <span className="text-muted-foreground">Exercise Routine:</span>
              <p className="font-medium capitalize">{profile.exercise_routine}</p>
            </div>
          )}
          {profile.dietary_preferences && profile.dietary_preferences.length > 0 && (
            <div className="col-span-2">
              <span className="text-muted-foreground">Dietary Preferences:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {profile.dietary_preferences.map((pref) => (
                  <Badge key={pref} variant="outline" className="text-xs">
                    {pref}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Separator />

      {/* Personality */}
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Personality</h4>
        <div className="grid gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">Personality Type:</span>
            <p className="font-medium">{profile.personality_type || "?"}/10 (1=Introverted, 10=Extroverted)</p>
          </div>
          {profile.personality_description && (
            <div>
              <span className="text-muted-foreground">Personality Description:</span>
              <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                {profile.personality_description}
              </p>
            </div>
          )}
        </div>
      </div>

      <Separator />

      {/* Openness */}
      <div className="space-y-3">
        <h4 className="font-semibold text-lg">Openness</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {profile.willing_to_marry_divorced !== undefined && (
            <div>
              <span className="text-muted-foreground">Open to Divorced:</span>
              <p className="font-medium">{profile.willing_to_marry_divorced ? "Yes" : "No"}</p>
            </div>
          )}
          {profile.willing_to_marry_with_children !== undefined && (
            <div>
              <span className="text-muted-foreground">Open to Partner with Children:</span>
              <p className="font-medium">{profile.willing_to_marry_with_children ? "Yes" : "No"}</p>
            </div>
          )}
          {profile.willing_to_marry_convert !== undefined && (
            <div>
              <span className="text-muted-foreground">Open to Convert:</span>
              <p className="font-medium">{profile.willing_to_marry_convert ? "Yes" : "No"}</p>
            </div>
          )}
        </div>
      </div>

      {profile.bio && (
        <>
          <Separator />
          <div className="space-y-3">
            <h4 className="font-semibold text-lg">About</h4>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {profile.bio}
            </p>
          </div>
        </>
      )}

      <Separator />

      {/* Seeking Preferences - Priority Weights */}
      {profile.seeking_preferences_weights && (
        <div className="space-y-3">
          <h4 className="font-semibold text-lg">What Matters Most to Them</h4>
          <PriorityBadges
            weights={profile.seeking_preferences_weights}
            firstName={firstName}
          />
        </div>
      )}

      <Separator />

      {/* What They're Looking For - Detailed */}
      <div className="space-y-4">
        <h4 className="font-semibold text-lg">What They're Looking For</h4>

        {/* Basic Criteria */}
        <Card className="p-4">
          <h5 className="font-medium mb-3">Basic Criteria</h5>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Age Range:</span>
              <p className="font-medium">{profile.seeking_age_min} - {profile.seeking_age_max}</p>
            </div>
            {profile.seeking_location_preference && (
              <div>
                <span className="text-muted-foreground">Location:</span>
                <p className="font-medium">{profile.seeking_location_preference}</p>
              </div>
            )}
            {profile.seeking_height_min_feet && (
              <div>
                <span className="text-muted-foreground">Height Range:</span>
                <p className="font-medium">
                  {profile.seeking_height_min_feet}'{profile.seeking_height_min_inches || 0}" -
                  {' '}{profile.seeking_height_max_feet}'{profile.seeking_height_max_inches || 0}"
                </p>
              </div>
            )}
            {profile.seeking_personality_min !== undefined && (
              <div>
                <span className="text-muted-foreground">Personality Range:</span>
                <p className="font-medium">
                  {profile.seeking_personality_min} - {profile.seeking_personality_max}
                </p>
              </div>
            )}
            {profile.seeking_financial_min !== undefined && (
              <div>
                <span className="text-muted-foreground">Min Financial Status:</span>
                <p className="font-medium">{profile.seeking_financial_min}/10</p>
              </div>
            )}
          </div>
        </Card>

        {/* Religious Criteria */}
        <Card className="p-4">
          <h5 className="font-medium mb-3">Religious Preferences</h5>
          <div className="grid gap-3 text-sm">
            {profile.seeking_religiosity && profile.seeking_religiosity.length > 0 && (
              <div>
                <span className="text-muted-foreground">Religiosity Levels:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {profile.seeking_religiosity.map((level) => (
                    <Badge key={level} variant="outline" className="text-xs capitalize">
                      {level}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {profile.seeking_sect && profile.seeking_sect.length > 0 && (
              <div>
                <span className="text-muted-foreground">Sects:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {profile.seeking_sect.map((sect) => (
                    <Badge key={sect} variant="outline" className="text-xs capitalize">
                      {sect}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {profile.seeking_islam_importance_min !== undefined && (
              <div>
                <span className="text-muted-foreground">Min Importance of Islam:</span>
                <p className="font-medium">{profile.seeking_islam_importance_min}/10</p>
              </div>
            )}
            {profile.seeking_masjid_attendance_min && (
              <div>
                <span className="text-muted-foreground">Min Masjid Attendance:</span>
                <p className="font-medium">{profile.seeking_masjid_attendance_min}</p>
              </div>
            )}
            {profile.gender === "male" && profile.seeking_hijab_preference && (
              <div>
                <span className="text-muted-foreground">Hijab Preference:</span>
                <p className="font-medium capitalize">{profile.seeking_hijab_preference}</p>
              </div>
            )}
            {profile.gender === "female" && profile.seeking_beard_preference && (
              <div>
                <span className="text-muted-foreground">Beard Preference:</span>
                <p className="font-medium capitalize">{profile.seeking_beard_preference}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Demographics */}
        <Card className="p-4">
          <h5 className="font-medium mb-3">Demographics</h5>
          <div className="grid gap-3 text-sm">
            {profile.seeking_ethnicity && profile.seeking_ethnicity.length > 0 && (
              <div>
                <span className="text-muted-foreground">Ethnicities:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {profile.seeking_ethnicity.map((ethnicity) => (
                    <Badge key={ethnicity} variant="outline" className="text-xs">
                      {ethnicity}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {profile.seeking_citizenship && profile.seeking_citizenship.length > 0 && (
              <div>
                <span className="text-muted-foreground">Citizenship:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {profile.seeking_citizenship.map((citizenship) => (
                    <Badge key={citizenship} variant="outline" className="text-xs">
                      {citizenship}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {profile.seeking_marital_status && profile.seeking_marital_status.length > 0 && (
              <div>
                <span className="text-muted-foreground">Marital Status:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {profile.seeking_marital_status.map((status) => (
                    <Badge key={status} variant="outline" className="text-xs capitalize">
                      {status}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Education */}
        {profile.seeking_education && profile.seeking_education.length > 0 && (
          <Card className="p-4">
            <h5 className="font-medium mb-3">Education</h5>
            <div className="flex flex-wrap gap-1">
              {profile.seeking_education.map((edu) => (
                <Badge key={edu} variant="outline" className="text-xs">
                  {edu}
                </Badge>
              ))}
            </div>
          </Card>
        )}

        {/* Openness Criteria */}
        <Card className="p-4">
          <h5 className="font-medium mb-3">Openness Criteria</h5>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {profile.seeking_divorced_ok !== undefined && (
              <div>
                <span className="text-muted-foreground">Open to Divorced:</span>
                <p className="font-medium">{profile.seeking_divorced_ok ? "Yes" : "No"}</p>
              </div>
            )}
            {profile.seeking_children_ok !== undefined && (
              <div>
                <span className="text-muted-foreground">Open to Children:</span>
                <p className="font-medium">{profile.seeking_children_ok ? "Yes" : "No"}</p>
              </div>
            )}
            {profile.seeking_convert_ok !== undefined && (
              <div>
                <span className="text-muted-foreground">Open to Convert:</span>
                <p className="font-medium">{profile.seeking_convert_ok ? "Yes" : "No"}</p>
              </div>
            )}
          </div>
        </Card>

        {profile.seeking_description && (
          <>
            <h5 className="font-medium mt-4">Additional Details</h5>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {profile.seeking_description}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
